import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OverviewModel } from '@shared/models/data/overview-model';
import { ProductInstanceModel } from '@shared/models/data/product-instance-model';
import { RemoveResidentModel } from '@shared/models/data/remove-resident';
import { ResidentModel } from '@shared/models/data/resident-model';
import { ResidentSearchModel } from '@shared/models/data/resident-search-model';
import { RosterModel } from '@shared/models/data/roster-model';
import { SeatModel } from '@shared/models/data/seat-model.component';
import { CurrentDateInputModel } from '@shared/models/data/current-date-model';
import { CurrentDateOutputModel } from '@shared/models/data/current-date-model';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AaosBaseService } from './aaos-base.service';
import { Constants } from './constants';
import { LoggingService } from './logging.service';
import { SessionService } from './session.service';
import { SaveSeatsModel } from '@shared/models/data/save-seats-model';
import { SeatUpdateResultModel } from '@shared/models/data/seat-update-result-model';
import { RemoveSeatsModel } from '@shared/models/data/remove-seats-model';
import { OrderSummaryModel } from '@shared/models/data/order-summary-model';
import { InstitutionSubscriptionModel } from '@shared/models/data/institution-subscription-model';
import { TransitionModel } from '@shared/models/data/transition-model';
import { PreferenceBaseModel } from '@shared/models/data/preferencebase-model';
import { ResidentSearchModelResponse } from '@shared/models/data/resident-search-model-response';
import { ProductModel } from '@shared/models/data/product-model';
import { ResidencyFacultyModel } from '@shared/models/data/residency-faculty-model';
import { FacultyModel } from '../models/data/faculty-model';
import { FacultySearchResponseModel } from '../models/data/faculty-search-response-model';

@Injectable({
  providedIn: 'root'
})
export class EnrollmentService extends AaosBaseService {

  public rosterChange: Subject<any> = new Subject();

  constructor(protected logging: LoggingService,
    protected http: HttpClient,
    private sessionService: SessionService) {
    super(logging, http);
  }
  public getOverViewModel(institutionId: string): Observable<OverviewModel> {
    return this.http.get(`${Constants.ApiUrl.GetOverViewModel}?${Constants.ApiParams.InstitutionId}=${institutionId}`,
     { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public getActiveProducts(): Observable<ProductModel[]> {
    return this.http.get(`${Constants.ApiUrl.GetActiveProducts}`, { responseType: 'json' })
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          return response;
        } else {
          return EMPTY;
        }
      })
    );
  }

  public getRosterModel(institutionId: string): Observable<RosterModel> {
    return this.http.get(`${Constants.ApiUrl.GetRosterModel}?${Constants.ApiParams.InstitutionId}=${institutionId}`,
     { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public saveSeat(seat: SeatModel) {
    return this.http.post(Constants.ApiUrl.SaveSeat, seat, { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public removeSeat(seat: SeatModel) {
    return this.http.post(Constants.ApiUrl.RemoveSeat, seat, { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }
  public getInstitutionInformation() {
    return this.http.get(Constants.ApiUrl.GetInstitutionInformation, { responseType: 'json' })
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          this.sessionService.setInstitutionId(response.institutionId);
          this.sessionService.setInstitutionName(response.institutionName);
          this.sessionService.setMcid(response.masterCustomerId);
          this.sessionService.setAcgme(response.acgme);
          this.sessionService.setRole(response.relationshipCodes[0]);
          this.sessionService.setInstitutionCountryCode(response.countryCode);
          return response;
        } else {
          return EMPTY;
        }
      })
    );
  }

  public searchResident(lastName: string,
                        firstName: string,
                        primaryEmail: string,
                        additionalEmail: string,
                        npi: string,
                        dateOfBirth: string,
                        businessPhone: string,
                        homePhone: string,
                        city: string,
                        stateProv: string,
                        maxLimit: number = -1): Observable<ResidentSearchModelResponse> {
    let requestUrl = `${Constants.ApiUrl.SearchResident}?`;
    requestUrl += `${Constants.ApiParams.LastName}=${lastName}&`;
    requestUrl += `${Constants.ApiParams.FirstName}=${firstName}&`;
    requestUrl += `${Constants.ApiParams.PrimaryEmail}=${primaryEmail}&`;
    requestUrl += `${Constants.ApiParams.AdditionalEmail}=${additionalEmail}&`;
    requestUrl += `${Constants.ApiParams.NPI}=${npi}&`;
    requestUrl += `${Constants.ApiParams.DateOfBirth}=${dateOfBirth}&`;
    requestUrl += `${Constants.ApiParams.BusinessPhone}=${businessPhone}&`;
    requestUrl += `${Constants.ApiParams.HomePhone}=${homePhone}&`;
    requestUrl += `${Constants.ApiParams.City}=${city}&`;
    requestUrl += `${Constants.ApiParams.StateProvince}=${stateProv}&`;
    requestUrl += `${Constants.ApiParams.MaxLimit}=${maxLimit}`;
    return this.http.get(requestUrl,
      { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

    public searchFaculty(lastName: string, firstName: string, primaryEmail: string, instutitionNumber: string,
       maxLimit: number = -1): Observable<FacultySearchResponseModel> {
    let requestUrl = `${Constants.ApiUrl.SearchFaculty}?`;
    requestUrl += `${Constants.ApiParams.LastName}=${lastName}&`;
    requestUrl += `${Constants.ApiParams.FirstName}=${firstName}&`;
    requestUrl += `${Constants.ApiParams.PrimaryEmail}=${primaryEmail}&`;
    requestUrl += `${Constants.ApiParams.InstitutionId}=${instutitionNumber}&`;
    requestUrl += `${Constants.ApiParams.MaxLimit}=${maxLimit}`;
    return this.http.get(requestUrl,
      { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public saveResident(resident: ResidentModel) {
    return this.http.post(Constants.ApiUrl.SaveResident, resident, { responseType: 'json' })
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          if (response.isValid) {
            this.rosterChange.next();
          }
          return response;
        } else {
          return EMPTY;
        }
      })
    );
  }

  public saveFaculty(faculty: FacultyModel) {
    return this.http.post(Constants.ApiUrl.SaveFaculty, faculty, {responseType: 'json'})
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((resp: any) => {
        if (resp !== null && resp !== undefined) {
          if (resp.isValid) {
            this.rosterChange.next();
          }
          return resp;
        } else {
          return EMPTY;
        }
      })
    );
  }

  public getProductInstance(institutionId: string, productId: string): Observable<ProductInstanceModel> {
    return this.http.get(`${Constants.ApiUrl.GetProductInstance}?${Constants.ApiParams.InstitutionId}=${institutionId}&${Constants.ApiParams.ProductId}=${productId}`,
    { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public removeFaculty(idToRemove: string): Observable<any> {

      const dto = {
        'contactWebRoleId' : idToRemove
      };

      return this.http.post(`${Constants.ApiUrl.RemoveFaculty}`, dto, { responseType: 'json'}).pipe(
          catchError(catchResponse => this.handleError(catchResponse)),
          map((resp: any) => {
          if (resp !== null && resp !== undefined) {
          if (resp === true) {
            this.rosterChange.next();
          }
            return resp;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public saveSeats(seatsModel: SaveSeatsModel): Observable<SeatUpdateResultModel> {
    if (seatsModel === null || seatsModel.seatModels.length === 0) {
      const emtpyUpdateModel = new SeatUpdateResultModel();
      emtpyUpdateModel.failedSeats = new Array<SeatModel>();
      emtpyUpdateModel.errors = new Array<string>();
      emtpyUpdateModel.isValid = true;
      return of(emtpyUpdateModel);
    }
    return this.http.post(`${Constants.ApiUrl.SaveSeats}`, seatsModel, { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            const seatUpdateModel = response as SeatUpdateResultModel;
            return seatUpdateModel;
          } else {
            return null;
          }
        })
      );
  }

  public removeSeats(seatsModel: RemoveSeatsModel): Observable<SeatUpdateResultModel> {
    if (seatsModel === null || seatsModel.seatModels.length === 0) {
      const emtpyUpdateModel = new SeatUpdateResultModel();
      emtpyUpdateModel.failedSeats = new Array<SeatModel>();
      emtpyUpdateModel.errors = new Array<string>();
      emtpyUpdateModel.isValid = true;
      return of(emtpyUpdateModel);
    }
    return this.http.post(`${Constants.ApiUrl.RemoveSeats}`, seatsModel, { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            const seatUpdateModel = response as SeatUpdateResultModel;
            return seatUpdateModel;
          } else {
            return null;
          }
        })
      );
  }

  public deleteResident(resident: RemoveResidentModel) {
    return this.http.post(Constants.ApiUrl.RemoveResident, resident,  { responseType: 'json' })
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          if (response.isValid) {
            this.rosterChange.next();
          }
          return response;
        } else {
          return EMPTY;
        }
      })
    );
  }

  getFaculty(institutionMCID: string): Observable<ResidencyFacultyModel[]> {
    return this.http.get(`${Constants.ApiUrl.GetFaculty}?${Constants.ApiParams.InstitutionId}=${institutionMCID}`, {responseType: 'json'})
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          return response;
        } else {
          return EMPTY;
        }
      })
    );
  }

  public getCurrentDate(id: string): Observable<CurrentDateOutputModel> {
    return this.http.get(`${Constants.ApiUrl.GetCurrentDate}?${Constants.ApiParams.Id}=${id}`, { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public setCurrentDate(date: CurrentDateInputModel) {
    return this.http.post(Constants.ApiUrl.SetCurrentDate, date,  { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  public clearCurrentDate(date: CurrentDateInputModel) {
    return this.http.post(Constants.ApiUrl.ClearCurrentDate, date,  { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response;
          } else {
            return EMPTY;
          }
        })
      );
  }

  getResidentModel(institutionId: string, residentId: string): Observable<ResidentModel> {
    return this.http.get(`${Constants.ApiUrl.GetResidentModel}?${Constants.ApiParams.InstitutionId}=${institutionId}&${Constants.ApiParams.ResidentId}=${residentId}`)
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            const residentModel = response as ResidentModel;
            return residentModel;
          } else {
            return null;
          }
        })
      );
  }

  getTransitionModel(institutionId: string, residentId: string): Observable<TransitionModel> {
    return this.http.get(`${Constants.ApiUrl.GetTransitionModel}?${Constants.ApiParams.InstitutionId}=${institutionId}&${Constants.ApiParams.ResidentId}=${residentId}`,
     { responseType: 'json' })
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          const transitionModel = response as TransitionModel;
          return transitionModel;
        } else {
          return null;
        }
      })
    );
  }

  getTransitionSubspecialties(): Observable<PreferenceBaseModel[]> {
    return this.http.get(Constants.ApiUrl.GetTransitionSubspecialties, { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response as PreferenceBaseModel[];
          } else {
            return null;
          }
        })
      );
  }

  public notifyOfResidentEnrollment(resident: ResidentModel) {
    return this.http.post(Constants.ApiUrl.NotifyEnrollment, resident)
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          const residentModel = response as ResidentModel;
          return residentModel;
        } else {
          return null;
        }
      })
    );
  }

  public getOrderSummaryModel(institutionId: string): Observable<OrderSummaryModel> {
    return this.http.get(`${Constants.ApiUrl.GetOrderSummaryModel}?${Constants.ApiParams.InstitutionId}=${institutionId}`,
     { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            const summaryModel = response as OrderSummaryModel;
            return summaryModel;
          } else {
            return null;
          }
        })
      );
  }

  public getInstitutionSubscriptions(institutionId: string, activeOnly: boolean): Observable<InstitutionSubscriptionModel[]> {
    return this.http.get(`${Constants.ApiUrl.GetInstitutionSubscriptions}?${Constants.ApiParams.InstitutionId}=${institutionId}` +
      `&${Constants.ApiParams.ActiveOnly}=${activeOnly}`,
     { responseType: 'json' })
      .pipe(
        catchError(catchResponse => this.handleError(catchResponse)),
        map((response: any) => {
          if (response !== null && response !== undefined) {
            return response as InstitutionSubscriptionModel[];
          } else {
            return null;
          }
        })
      );
  }

  saveTransition(transition: TransitionModel) {
    return this.http.post(Constants.ApiUrl.saveTransition, transition, { responseType: 'json' })
    .pipe(
      catchError(catchResponse => this.handleError(catchResponse)),
      map((response: any) => {
        if (response !== null && response !== undefined) {
          if (response.isValid) {
            this.rosterChange.next();
          }
          return response;
        } else {
          return EMPTY;
        }
      })
    );
  }

}
